import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Brain, AlertTriangle, Loader2, RefreshCw, ChevronDown, ChevronRight,
  Zap, ShieldAlert, TrendingUp,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useCurrency } from '@/lib/useCurrency';
import api from '@/lib/api';
import { toast } from 'sonner';

export function MLInsightsPanel() {
  const { symbol: cs } = useCurrency();
  const [status, setStatus] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/ml/status');
      setStatus(res.data);
      if (res.data.anomaly_detector?.trained) {
        try {
          const anomRes = await api.get('/ml/anomalies?limit=10');
          setAnomalies(anomRes.data.anomalies || []);
        } catch (anomErr) {
          console.error('Error fetching anomalies:', anomErr);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching ML status:', err);
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleTrain = async () => {
    setTraining(true);
    try {
      const res = await api.post('/ml/train');
      toast.success(`Models trained — classifier: ${(res.data.classifier?.accuracy * 100).toFixed(0)}% accuracy, anomalies: ${res.data.anomaly_detector?.anomalies_found} detected`);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Training failed');
    } finally {
      setTraining(false);
    }
  };

  const isTrained = status?.classifier?.trained || status?.anomaly_detector?.trained;

  if (loading) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card data-testid="ml-insights-panel">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                <div>
                  <CardTitle className="text-sm">ML Insights</CardTitle>
                  <CardDescription className="text-xs">
                    {isTrained
                      ? `${anomalies.length} anomalies detected • Classifier: ${((status?.classifier?.accuracy || 0) * 100).toFixed(0)}% accuracy`
                      : 'Train models on your data for anomaly detection and smart classification'
                    }
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {anomalies.length > 0 && (
                  <Badge variant="destructive" className="text-[10px]" data-testid="anomaly-count-badge">
                    {anomalies.length} anomalies
                  </Badge>
                )}
                {isTrained && (
                  <Badge variant="secondary" className="text-[10px]">
                    <Zap className="w-3 h-3 mr-0.5" /> Trained
                  </Badge>
                )}
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            {/* Training controls */}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={isTrained ? 'outline' : 'default'}
                onClick={handleTrain}
                disabled={training}
                data-testid="ml-train-btn"
              >
                {training ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                {training ? 'Training...' : isTrained ? 'Retrain Models' : 'Train Models'}
              </Button>
              {status?.classifier?.trained && (
                <span className="text-[10px] text-muted-foreground">
                  Classifier: {((status.classifier.accuracy || 0) * 100).toFixed(0)}% on {status.classifier.training_samples} samples
                  • {status.classifier.category_count} categories
                </span>
              )}
            </div>

            {/* Model status cards */}
            {isTrained && (
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const acc = (status?.classifier?.accuracy || 0) * 100;
                  const quality = acc >= 80 ? { label: 'Reliable', color: 'text-emerald-600', bg: 'bg-emerald-50/50 dark:bg-emerald-950/20', border: 'border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500' }
                    : acc >= 60 ? { label: 'Moderate', color: 'text-amber-600', bg: 'bg-amber-50/50 dark:bg-amber-950/20', border: 'border-amber-200 dark:border-amber-800', dot: 'bg-amber-500' }
                    : { label: 'Insufficient', color: 'text-red-600', bg: 'bg-red-50/50 dark:bg-red-950/20', border: 'border-red-200 dark:border-red-800', dot: 'bg-red-500' };
                  return (
                    <div className={`p-2 rounded-md border ${quality.bg} ${quality.border}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <Brain className="w-3 h-3 text-purple-500" />
                          <span className="text-[11px] font-medium">Classification</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${quality.dot}`} />
                          <span className={`text-[9px] font-medium ${quality.color}`}>{quality.label}</span>
                        </div>
                      </div>
                      <p className={`text-lg font-bold font-mono ${quality.color}`}>{acc.toFixed(0)}%</p>
                      <p className="text-[10px] text-muted-foreground">accuracy</p>
                      {acc < 60 && (
                        <p className="text-[9px] text-red-500 mt-1">Below 60% — suggestions suppressed. More data or fewer categories needed.</p>
                      )}
                    </div>
                  );
                })()}
                <div className="p-2 rounded-md border bg-red-50/50 dark:bg-red-950/20">
                  <div className="flex items-center gap-1.5 mb-1">
                    <ShieldAlert className="w-3 h-3 text-red-500" />
                    <span className="text-[11px] font-medium">Anomalies</span>
                  </div>
                  <p className="text-lg font-bold font-mono text-red-600">{status?.anomaly_detector?.anomalies_detected || 0}</p>
                  <p className="text-[10px] text-muted-foreground">{status?.anomaly_detector?.anomaly_rate || 0}% anomaly rate</p>
                </div>
              </div>
            )}

            {/* Anomaly list */}
            {anomalies.length > 0 && (
              <div className="space-y-1" data-testid="anomaly-list">
                <p className="text-xs font-medium flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                  Top Anomalies
                </p>
                <div className="space-y-1 max-h-[220px] overflow-y-auto">
                  {anomalies.map((a, i) => (
                    <div key={a.id || i} className="flex items-center gap-2 p-2 rounded-md border border-amber-200/50 dark:border-amber-800/30 bg-amber-50/30 dark:bg-amber-950/10 text-xs" data-testid={`anomaly-row-${i}`}>
                      <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{a.supplier_name}</span>
                          <span className="text-muted-foreground">{cs}{a.spend_amount?.toLocaleString()}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{a.reasons?.join(' • ')}</p>
                      </div>
                      <Badge variant="outline" className="text-[9px] shrink-0">{a.category}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isTrained && anomalies.length === 0 && (
              <div className="text-center py-3 text-xs text-muted-foreground">
                <TrendingUp className="w-6 h-6 mx-auto mb-1 text-emerald-500" />
                No anomalies detected — your spend patterns look healthy
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
